import React, { useContext, useState, useEffect } from 'react';
import { CustomDialog } from '../common/custom-dialog';
import { useTranslation } from '../../../../hooks/useTranslation';
import {
  Box,
  Button,
  Checkbox,
  createStyles,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import colors from '../../../../themes/main-colors';
import PhoneTextField from '../../../phone-text-field';
import EmailTextField from '../../../email-text-field';
import { useOutageLeftPanelState } from '../../providers/OutageLeftPanelStateProvider';
import { parsePhoneNumber } from '../../../../util/format';
import { useReportOutageApi } from '../../hooks/useReportOutageApi';
import {
  NotificationType,
  DeliveryType,
} from '../../../../__generated__/pge-types';
import { useGetUserAccounts } from '../../hooks/useGetUserAccounts';
import { AccountWithOutageInfo } from '../../types';
import { NotificationsContext } from '../../../../providers/NotificationsProvider';
import useWrapWithLoader from '../../../../hooks/useWrapWithLoading';
import ReactMarkdown from 'react-markdown/with-html';
import ROUTES from '../../../../routes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    boxStyles: {
      padding: '1rem',
    },
    innerBox: {
      background: colors.white,
      padding: '1rem',
    },
    containerBox: {
      display: 'flex',
      marginTop: theme.spacing(2),
      gap: '1rem',
      [theme.breakpoints.down('xs')]: {
        flexDirection: 'column',
        paddingBottom: 22,
        marginLeft: 0,
      },
    },
    savePreferencesButton: {
      borderRadius: '20px',
      background: colors.orange,
      color: colors.white,
      fontSize: 16,
      fontWeight: 600,
      width: '45%',
      '&:hover': {
        backgroundColor: colors.orange,
        borderColor: colors.orange,
      },
      [theme.breakpoints.down('sm')]: {
        width: '90%',
      },
    },
    savePreferencesButtonDisabled: {
      borderRadius: '20px',
      background: colors.gray300,
      color: colors.white,
      fontSize: 16,
      fontWeight: 600,
      width: '45%',
      [theme.breakpoints.down('sm')]: {
        width: '90%',
      },
    },
    buttonComp: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '2rem',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column-reverse',
        gap: '15px',
        marginTop: 0,
      },
    },
    cancelButton: {
      fontSize: 16,
      color: colors.sparkBlue,
      fontWeight: 600,
      width: '50%',
    },
  }),
);
interface OutageNotificationsProps {
  open: boolean;
  setOpen: (value: boolean) => void;
}

export const OutageNotifications = ({
  open,
  setOpen,
}: OutageNotificationsProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    selectedAccountForOutage,
    setSelectedAccountForOutage,
  } = useOutageLeftPanelState();
  const { commPreferenceInfo, accountNumber, encryptedPersonId } =
    selectedAccountForOutage || {};
  const { submitCommPreferenceForLoggedInUser } = useReportOutageApi();
  const { refetch } = useGetUserAccounts();
  const notification = useContext(NotificationsContext);
  const { wrapWithLoader } = useWrapWithLoader();

  const [deliveryPreferencesInfo, setDeliveryPreferencesInfo] = useState<
    Record<
      string,
      Array<{
        value?: string;
        isSelected?: boolean;
        encryptedContactId?: string | null;
        encryptedPreferenceId?: string | null;
      }>
    >
  >({});
  const [
    initialDeliveryPreferencesSnapshot,
    setInitialDeliveryPreferencesSnapshot,
  ] = useState<string>('');

  useEffect(() => {
    const deliveryTypeDetails = Array.isArray(commPreferenceInfo)
      ? commPreferenceInfo.flatMap((cp: any) => cp?.deliveryTypeDetails || [])
      : [];

    const newPrefs: Record<
      string,
      Array<{ value?: string; isSelected?: boolean }>
    > = {};

    deliveryTypeDetails.forEach((d: any) => {
      const deliveryType = d?.deliveryType;
      const contactDetails = Array.isArray(d?.contactDetails)
        ? d.contactDetails.map((c: any) => ({
            value:
              typeof c?.value === 'string'
                ? deliveryType === 'SMS'
                  ? parsePhoneNumber(c.value)
                  : c.value
                : undefined,
            isSelected: !!c?.isSelected,
            encryptedContactId: c?.encryptedContactId ?? null,
            encryptedPreferenceId: c?.encryptedPreferenceId ?? null,
          }))
        : [];
      if (deliveryType) {
        newPrefs[deliveryType] = (newPrefs[deliveryType] || []).concat(
          contactDetails,
        );
      }
    });

    setDeliveryPreferencesInfo(newPrefs);
    try {
      setInitialDeliveryPreferencesSnapshot(JSON.stringify(newPrefs));
    } catch (e) {
      setInitialDeliveryPreferencesSnapshot('');
    }
  }, [commPreferenceInfo]);
  const isDirty = React.useMemo(() => {
    try {
      return (
        JSON.stringify(deliveryPreferencesInfo) !==
        initialDeliveryPreferencesSnapshot
      );
    } catch (e) {
      return false;
    }
  }, [deliveryPreferencesInfo, initialDeliveryPreferencesSnapshot]);
  const smsPref = deliveryPreferencesInfo['SMS'];
  const emailPref = deliveryPreferencesInfo['EMAIL']?.[0];

  const handleToggleDelivery = (deliveryType: string) => (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setDeliveryPreferencesInfo(prev => {
      const prevList = prev[deliveryType] ? [...prev[deliveryType]] : [];
      if (prevList.length === 0) {
        return prev;
      }
      const newList = prevList.map((item, idx) =>
        idx === 0 ? { ...item, isSelected: checked } : item,
      );
      return { ...prev, [deliveryType]: newList };
    });
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleUpdate = wrapWithLoader(async () => {
    if (!accountNumber || !encryptedPersonId) {
      console.warn(
        'Missing accountNumber or encryptedPersonId - cannot submit preferences',
      );
      return;
    }

    try {
      const deliveryTypesPayload = Object.entries(deliveryPreferencesInfo).map(
        ([deliveryTypeKey, contacts]) => {
          let deliveryTypeEnum: DeliveryType = DeliveryType.Sms;
          const key = String(deliveryTypeKey).toUpperCase();
          if (key === 'SMS') {
            deliveryTypeEnum = DeliveryType.Sms;
          } else if (key === 'EMAIL') {
            deliveryTypeEnum = DeliveryType.Email;
          } else if (key === 'PUSH') {
            deliveryTypeEnum = DeliveryType.Push;
          }

          const contactsPayload = (contacts || []).map(c => {
            const encryptedContactId = c.encryptedContactId ?? '';
            const encryptedPreferenceId = c.encryptedPreferenceId ?? undefined;
            return {
              encryptedContactId,
              encryptedPreferenceId,
              isSelected: !!c.isSelected,
              value: c.value ?? '',
            };
          });

          return {
            deliveryType: deliveryTypeEnum,
            contacts: contactsPayload,
          };
        },
      );

      const payload = {
        accountNumber: accountNumber,
        encryptedPersonId: encryptedPersonId,
        notificationTypes: [
          {
            notificationType: NotificationType.Rest,
            deliveryTypes: deliveryTypesPayload,
          },
        ],
      };
      const response = await submitCommPreferenceForLoggedInUser(
        payload as any,
      );
      if (response?.submitCommPreferences?.success) {
        notification.setState({
          isOpen: true,
          severity: 'success',
          message: t('UPDATED_SUCCESSFULLY'),
        });
        const res = await refetch();
        const accounts = res?.data?.getAccountDetails || [];
        const accountDetail =
          accounts.length > 1
            ? accounts.find(
                (acc: any) => acc.accountNumber === accountNumber,
              ) || accounts[0]
            : accounts[0];
        if (accountDetail) {
          const accountWithOutageInfo: AccountWithOutageInfo = {
            ...(selectedAccountForOutage as AccountWithOutageInfo),
            commPreferenceInfo: (accountDetail.commPreferences || []).filter(
              (c: any) => c !== null,
            ) as any[],
          };
          setSelectedAccountForOutage(accountWithOutageInfo);
        }
      }
      setOpen(false);
    } catch (err) {
      notification.setState({
        isOpen: true,
        severity: 'error',
        message: t('UPDATE_FAILED'),
      });
    }
  });

  return (
    <CustomDialog
      open={open}
      onClose={handleClose}
      title={t('NOTIFICATION_OPTIONS')}
    >
      <Box className={classes.boxStyles}>
        <Box className={classes.innerBox}>
          {emailPref?.value ? (
            <Typography>{t('HOW_TO_NOTIFY_POWER_RESTORED')}</Typography>
          ) : (
            <Typography>
              <ReactMarkdown
                source={t('DO_NOT_HAVE_COMM_PREFERENCES', {
                  PAGE_LINK: ROUTES.UPDATE_INFORMATION,
                })}
                escapeHtml={false}
              />
            </Typography>
          )}
          <Grid xs={12} md={12} className={classes.containerBox}>
            {smsPref?.length > 0 && (
              <Grid xs={12} md={12}>
                <Box>
                  <Typography variant={'subtitle1'} noWrap>
                    {t('TEXT_MESSAGE')}
                  </Typography>
                  {smsPref.map(
                    (sms, index) =>
                      sms?.value && (
                        <Box
                          key={`sms-${index}`}
                          display={'flex'}
                          flexDirection={'row'}
                          alignItems={'center'}
                        >
                          <Checkbox
                            value="secondary"
                            name={`receiveText-${index}`}
                            color={'primary'}
                            checked={!!sms?.isSelected}
                            onChange={(e, checked) => {
                              setDeliveryPreferencesInfo(prev => {
                                const prevList = prev['SMS']
                                  ? [...prev['SMS']]
                                  : [];
                                const newList = prevList.map((item, idx) =>
                                  idx === index
                                    ? { ...item, isSelected: checked }
                                    : item,
                                );
                                return { ...prev, SMS: newList };
                              });
                            }}
                          />
                          <PhoneTextField
                            size={'small'}
                            placeholder={t('PHONE_NUMBER')}
                            label={''}
                            disabled={true}
                            name={`textNumber-${index}`}
                            autoComplete={'telephone'}
                            type={'number'}
                            defaultValue={sms?.value}
                          />
                        </Box>
                      ),
                  )}
                </Box>
              </Grid>
            )}
            {emailPref?.value && (
              <Grid xs={12} md={12}>
                <Box>
                  <Typography variant={'subtitle1'} noWrap>
                    {t('EMAIL')}
                  </Typography>
                  <Box
                    display={'flex'}
                    flexDirection={'row'}
                    alignItems={'center'}
                  >
                    <Checkbox
                      value="secondary"
                      name="receiveEmail"
                      color={'primary'}
                      checked={!!emailPref?.isSelected}
                      onChange={handleToggleDelivery('EMAIL')}
                    />
                    <EmailTextField
                      placeholder={t('EMAIL_ADDRESS')}
                      label={''}
                      size={'small'}
                      disabled={true}
                      name="email"
                      defaultValue={emailPref?.value ?? ''}
                    />
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
          <Grid className={classes.buttonComp}>
            <Button
              className={classes.cancelButton}
              onClick={() => handleClose()}
            >
              {t('CANCEL')}
            </Button>
            <Button
              className={clsx(
                classes.savePreferencesButton,
                !isDirty && classes.savePreferencesButtonDisabled,
              )}
              disabled={!isDirty}
              onClick={() => handleUpdate()}
            >
              {t('SAVE_OPTIONS')}
            </Button>
          </Grid>
        </Box>
      </Box>
    </CustomDialog>
  );
};
